import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import type { SvgIconComponent } from '@material-ui/icons';
import GroupAddIcon from '@material-ui/icons/GroupAdd';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import UpdateIcon from '@material-ui/icons/Update';
import React from 'react';
import useSWR from 'swr';

import { Alert } from 'src/common/ui/components';
import { useClinicActions } from 'src/dropInClinic/ClinicActions.hook';
import { requestTypeMap } from 'src/dropInClinic/RequestType';
import { GET_PATIENT_DROP_IN_HOURS } from 'src/dropInClinic/queries/getPatientDropInHours.gql';
import { CLINIC_ACTIONS_OVERRIDE } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { DropInClinicPatientHoursQuery } from 'src/generated/gql/graphql';
import Colors from 'src/nightingale/Colors';
import { PageTitle } from 'src/nightingale/components/common/PageTitle/PageTitle';

type RequestType = keyof typeof requestTypeMap;

const V2_REQUEST_TYPES: Array<
  Extract<RequestType, 'FOLLOW_UP' | 'INTAKE' | 'PEER_VISIT' | 'REENGAGEMENT'>
> = ['FOLLOW_UP', 'INTAKE', 'PEER_VISIT', 'REENGAGEMENT'];

const iconMap: Record<(typeof V2_REQUEST_TYPES)[number], SvgIconComponent> = {
  FOLLOW_UP: UpdateIcon,
  INTAKE: GroupAddIcon,
  PEER_VISIT: SupervisedUserCircleIcon,
  REENGAGEMENT: RotateRightIcon,
};

const HoursDisplay = ({ hoursData }) => {
  if (!hoursData) return <></>;

  return (
    <div
      style={{
        marginTop: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        color: Colors.Gray7,
        fontFamily: '"Nunito", "Nunito Sans"',
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: 1.1,
      }}
    >
      Pop-In Clinic hours:
      {Object.entries(hoursData)
        .filter(([, queueHours]) => Array.isArray(queueHours) && queueHours.length)
        .map(([queueType, queueHours]) => (
          <div key={queueType} style={{ margin: 8 }}>
            {queueType}
            {(queueHours as string[]).map(hours => (
              <div key={hours}>
                {'\n \u2022 '}
                {hours}
              </div>
            ))}
          </div>
        ))}
    </div>
  );
};

export const ClinicActionsV2 = ({ patientId }: { patientId: string }) => {
  const { state, createRequest } = useClinicActions(patientId);
  const isLoading = state?.state === 'loading';
  const loadingRequestType = isLoading ? state?.requestType : null;
  const spinner = <CircularProgress size={20} />;

  const { data: hoursData } = useSWR<DropInClinicPatientHoursQuery>([
    GET_PATIENT_DROP_IN_HOURS,
    { patientId },
  ]);

  const canOverrideQueue = useFeatureFlag(CLINIC_ACTIONS_OVERRIDE);

  return (
    <div style={{ maxWidth: 950 }}>
      <PageTitle>Clinic Actions</PageTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {V2_REQUEST_TYPES.map(requestType => {
          const Icon = iconMap[requestType];
          return (
            <Button
              key={requestType}
              color="secondary"
              variant="contained"
              startIcon={isLoading && loadingRequestType === requestType ? spinner : <Icon />}
              disabled={isLoading || !canOverrideQueue}
              onClick={() => createRequest(requestType)}
              style={{ width: '400px' }}
            >
              {requestTypeMap[requestType]}
            </Button>
          );
        })}
        {state?.state === 'error' && (
          <Alert
            title="Error creating Pop-In Clinic request"
            message={state.errors?.join(', ') || 'Error adding patient to queue.'}
            severity="error"
          />
        )}
        {state?.state === 'no_availability' && (
          <Alert
            title="Error creating Pop-In Clinic request"
            message="No availability for patient."
            severity="error"
          />
        )}
        {state?.state === 'success' && (
          <>
            <Alert severity="success" title="Patient added to queue" />
            {state.warning && <Alert severity="info" message={state.warning} />}
          </>
        )}
        <HoursDisplay hoursData={hoursData?.getPatientDropInClinicHours} />
      </div>
    </div>
  );
};
